import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@sportscanner/ui";
import { apiGet } from "@/lib/api";

interface ClubSummary {
  id: string;
  slug: string;
  name: string;
  address: string;
}

interface Props {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export default function ClubAutocomplete({
  value,
  onChange,
  placeholder,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);

  const searchQuery = useQuery({
    queryKey: ["admin", "clubs", "search", debouncedQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedQuery) params.set("q", debouncedQuery);
      params.set("limit", "10");
      const data = await apiGet<{ clubs: ClubSummary[] }>(
        `/api/admin/clubs?${params.toString()}`,
      );
      return data.clubs;
    },
    enabled: open,
    staleTime: 60_000,
  });

  // Resolve display name for the currently selected clubId.
  const selectedQuery = useQuery({
    queryKey: ["admin", "clubs", value],
    queryFn: async () => {
      const data = await apiGet<{ club: ClubSummary }>(
        `/api/admin/clubs/${value}`,
      );
      return data.club;
    },
    enabled: !!value,
    staleTime: 60_000,
  });

  const triggerLabel = value
    ? (selectedQuery.data?.name ??
      (selectedQuery.isLoading ? "…" : t("autocomplete.searchPlaceholder")))
    : (placeholder ?? t("autocomplete.searchPlaceholder"));

  return (
    <div className="flex items-center gap-2 min-w-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="flex-1 min-w-0 justify-between rounded-lg border-neutral-200 font-normal"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t("autocomplete.searchPlaceholder")}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {searchQuery.isLoading && (
                <div className="py-6 text-center text-sm text-neutral-500">
                  {t("common.loading")}
                </div>
              )}
              {!searchQuery.isLoading && (
                <>
                  <CommandEmpty>{t("autocomplete.noResults")}</CommandEmpty>
                  {(searchQuery.data ?? []).map((club) => (
                    <CommandItem
                      key={club.id}
                      value={club.id}
                      onSelect={() => {
                        onChange(club.id);
                        setOpen(false);
                        setQuery("");
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{club.name}</span>
                        <span className="text-xs text-neutral-500">
                          {club.address}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onChange(null)}
          aria-label={t("autocomplete.clear")}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
